/**
 * Structured console diagnostics for aniOS (workspace shell, window chunks, bootstrap).
 * Prefer these over ad-hoc logs so support can filter on `[aniOS]` in DevTools.
 */

const LOG_PREFIX = "[aniOS]";

export type AniOsDiagnosticLevel = "warn" | "error";

export interface AniOsDiagnosticPayload {
  [key: string]: unknown;
}

function emit(
  level: AniOsDiagnosticLevel,
  category: string,
  message: string,
  detail?: AniOsDiagnosticPayload,
): void {
  const line = `${LOG_PREFIX} [${category}] ${message}`;
  if (detail && Object.keys(detail).length > 0) {
    if (level === "error") {
      console.error(line, detail);
    } else {
      console.warn(line, detail);
    }
    return;
  }
  if (level === "error") {
    console.error(line);
  } else {
    console.warn(line);
  }
}

const ANIOS_SHELL_TEST_IDS = [
  "os-layout",
  "os-layout-loading",
  "os-layout-gate-bootstrap",
] as const;

function isAniOsLayoutPreferredInStorage(): boolean {
  if (typeof window === "undefined") return false;
  try {
    return localStorage.getItem("ani_layout_mode") !== "classic";
  } catch {
    return true;
  }
}

function isAniOsShellDomActive(): boolean {
  if (typeof document === "undefined") return false;
  return ANIOS_SHELL_TEST_IDS.some(
    (id) => document.querySelector(`[data-testid="${id}"]`) != null,
  );
}

/** True when the aniOS desktop shell is mounted (not classic sidebar layout). */
export function isAniOsShellActive(): boolean {
  if (isAniOsShellDomActive()) {
    return true;
  }
  return isAniOsLayoutPreferredInStorage();
}

/** Whether a stale-chunk incident should trigger a full-page reload (classic shell only). */
export function shouldAutoReloadPageForChunkLoadError(): boolean {
  return !isAniOsShellDomActive();
}

export function logAniOsDiagnostic(
  category: string,
  message: string,
  detail?: AniOsDiagnosticPayload,
  level: AniOsDiagnosticLevel = "warn",
): void {
  emit(level, category, message, detail);
}

function errorMessage(error: unknown): string {
  if (error instanceof Error) return error.message;
  if (typeof error === "string") return error;
  return String(error);
}

/** Stale or missing Vite chunk while opening a lazy OS window or route. */
export function logAniOsChunkLoadError(
  source: string,
  error: unknown,
  detail?: AniOsDiagnosticPayload,
): void {
  emit(
    "error",
    "chunk-load",
    errorMessage(error),
    {
      source,
      shellActive: isAniOsShellActive(),
      ...detail,
    },
  );
}

/** Non-chunk render failure inside an OS window. */
export function logAniOsWindowRenderError(
  error: unknown,
  detail?: AniOsDiagnosticPayload,
): void {
  emit("error", "window-render", errorMessage(error), detail);
}

/** Workspace provider bootstrap / restore milestones. */
export function logAniOsWorkspaceBootstrap(
  phase: string,
  detail?: AniOsDiagnosticPayload,
): void {
  emit("warn", "workspace", phase, detail);
}

/** Full-page reload triggered to recover from stale assets. */
export function logAniOsChunkReload(
  source: string,
  detail?: AniOsDiagnosticPayload,
): void {
  emit("warn", "chunk-reload", `Full page reload (${source})`, detail);
}
